// SPDX-License-Identifier: Elastic-2.0
/**
 * SignInPage — re-auth landing at `/onboarding/account`.
 *
 * The page serves two complementary purposes:
 *
 * 1. **Fixture-mode bridge** — when running `npm run dev:fixture`,
 *    sign-out drops the user here without a way back into the SPA.
 *    The two CTAs below re-establish a seeded session OR restart
 *    onboarding from A1, so the dev tester is never stranded.
 *
 * 2. **Production cloud re-auth** — outside fixture mode, the page
 *    renders the real `<SignInForm />` (F-613): email + password,
 *    validation, error states, loading state, and a "Forgot password?"
 *    link. On success it commits the cloud session and returns the user
 *    to where the `AuthGuard` redirect came from.
 *
 * The fixture-mode branch dynamically imports `@/fixtures/seed` so
 * the fixture module never enters the production bundle — Vite tree-
 * shakes the dynamic-import branch into a separate chunk that only
 * loads when the runtime guard fires.
 */
import { Button } from "@/components/Button/Button";
import { Card } from "@/components/Card/Card";
import { MirlaiMark } from "@/components/MirlaiMark/MirlaiMark";

import { SignInForm } from "./SignInForm";
import styles from "./SignInPage.module.css";

const FIXTURE_MODE_ENABLED =
  (import.meta.env.VITE_FIXTURE_MODE as string | undefined) === "1";

/**
 * localStorage keys the "Restart onboarding" path wipes.
 *
 * Auth tokens + onboarding flags + the wizard's own persisted form
 * state. The bug we're fixing here: `onboarding-state` (the
 * persisted wizard step + form data, set by useOnboardingStore)
 * survived a previous restart and dropped the user on whatever step
 * they last reached, not A1.
 *
 * Mirrored verbatim from `features/auth/tokenStorage.ts` +
 * `features/onboarding/useOnboardingStore.ts` so adding a new key
 * upstream surfaces as a real restart leaving stale state — easier
 * to spot than a `clearAll()` helper that gets stale silently.
 */
const RESTART_STORAGE_KEYS = [
  "mirlai_jwt",
  "mirlai_refresh",
  "mirlai_jwt_local",
  "mirlai_instance",
  "onboarding-complete",
  "onboarding-state",
  "dashboard-visit-count",
  "install-prompt-dismissed-until",
] as const;

export function SignInPage(): JSX.Element {
  async function handleResumeDemo(): Promise<void> {
    // Dynamic import — production builds never load the fixture
    // module because the `if (FIXTURE_MODE_ENABLED)` guard above
    // prevents the call site from firing.
    const { seedFixtureSession } = await import("@/fixtures/seed");
    seedFixtureSession();
    // Hard reload so initAuth() re-hydrates from the freshly seeded
    // storage — calling navigate() alone keeps the in-memory auth
    // store empty until next mount.
    window.location.assign("/dashboard");
  }

  function handleRestartOnboarding(): void {
    try {
      for (const key of RESTART_STORAGE_KEYS) localStorage.removeItem(key);
    } catch {
      // Private mode / quota — silently degrade.
    }
    // Hard reload so AuthGuard re-checks against the cleared state
    // and the onboarding store rebuilds from scratch.
    window.location.assign("/onboarding");
  }

  return (
    <div className={styles.shell} data-testid="signin-page">
      <Card variant="elevated" padding="lg" className={styles.card}>
        {/* No `title` prop → MirlaiMark renders in decorative mode
            with its own aria-hidden. */}
        <MirlaiMark size={48} />
        {FIXTURE_MODE_ENABLED ? (
          // Fixture-mode bridge — unchanged from the placeholder era:
          // backend calls are mocked, so offer a way back into the SPA.
          <>
            <h1 className={styles.title}>Demo mode</h1>
            <p className={styles.body}>
              You're running the SPA in fixture mode — backend calls are
              mocked. Pick a way back in:
            </p>
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={() => {
                  void handleResumeDemo();
                }}
                data-testid="signin-resume-demo"
              >
                Continue as demo user
              </Button>
              <Button
                variant="ghost"
                onClick={handleRestartOnboarding}
                data-testid="signin-restart-onboarding"
              >
                Restart onboarding
              </Button>
            </div>
          </>
        ) : (
          // F-613: the real cloud re-auth form (title + intro live inside
          // SignInForm so there's no duplicate page heading).
          <SignInForm />
        )}
      </Card>
    </div>
  );
}
